import React, { useCallback } from 'react';
import { Box } from 'utils/style/extendable';
import { NumberOrNull } from 'store/entity/types';
import { AddNewChartBtn } from './styles';
import Dropdown from 'components/Dropdown';
import Icon from 'components/Icon';
import SeriesEditor from './SeriesEditor';
import { DragDropContext, Droppable, DropResult } from 'react-beautiful-dnd';
import { ITags } from 'store/providers/providerTypes';
import {
  IReportField,
  IScoreRangeReportField,
  ISeries,
  ISurveyView,
} from 'store/survey/surveyTypes';
import { IOpenItem } from 'store/entity/IOpenItem';
import { useTranslation } from 'react-i18next';
import { languageDirection } from '../../i18n';

const { Menu, MenuItem } = Dropdown;

interface IChartSeriesProps {
  tags: ITags;
  series: ISeries[];
  surveyItem?: ISurveyView;
  reportItem?: IOpenItem;
  selectedReport: NumberOrNull; // null for nothing, positive numbers for editing, -1 for creating
  onAddSeries: (seriesType: number | string) => void;
  onChangeItem: (seriesIndex: number, series: ISeries) => void;
  onRemoveItem: (seriesIndex: number) => void;
  onMoveItem: (fromIndex: number, toIndex: number) => void;
  editFields: Array<IReportField | IScoreRangeReportField>;
}

const ChartSeries: React.FC<IChartSeriesProps> = ({
  onAddSeries,
  onChangeItem,
  onRemoveItem,
  series,
  surveyItem,
  tags,
  onMoveItem,
  editFields,
}) => {
  const handleAddSeries = (seriesType: string) => {
    onAddSeries(seriesType);
  };
  const { t } = useTranslation('survey');
  const handleAddSeriesCallback = useCallback(handleAddSeries, [onAddSeries]);

  const hasScoreFilter = !!editFields.find((field) => field.type === 'score');

  return (
    <Box style={{ marginBottom: '1rem' }}>
      <Dropdown
        marginToTarget={4}
        placement={
          languageDirection() === 'rtl' ? 'bottom-end' : 'bottom-start'
        }
        width={150}
        target={
          <AddNewChartBtn size="small">
            {t('addNewChart')}
            <Icon type="chevron-down" />
          </AddNewChartBtn>
        }
      >
        <Menu>
          <MenuItem>
            <span onClick={() => handleAddSeriesCallback('all')}>
              {t('all')}
            </span>
          </MenuItem>
          {!hasScoreFilter && (
            <>
              <MenuItem>
                <span onClick={() => handleAddSeriesCallback('tag')}>
                  {t('tag')}
                </span>
              </MenuItem>
            </>
          )}
          <MenuItem>
            <span onClick={() => handleAddSeriesCallback('question')}>
              {t('question')}
            </span>
          </MenuItem>
        </Menu>
      </Dropdown>
      {/*@ts-expect-error JSX error*/}
      <DragDropContext
        onDragEnd={(result: DropResult) => {
          onMoveItem(result.source!.index, result.destination!.index);
        }}
      >
        <Droppable droppableId="droppable-1">
          {(provided) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              style={{ maxHeight: '25rem', overflow: 'auto' }}
            >
              {series.map((item, index) => (
                <SeriesEditor
                  key={index}
                  index={index}
                  series={item}
                  survey={surveyItem}
                  onChangeItem={onChangeItem}
                  onRemoveItem={onRemoveItem}
                  tags={tags}
                  hasScoreFilter={hasScoreFilter}
                />
              ))}

              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </Box>
  );
};

export default React.memo(ChartSeries);
